import { apiInfo } from '@/renderer/util/notify';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useCategories, useMenuItems } from '@hooks/menu';
import { selectOutletId } from '@store/auth/authSelectors';
import {
  openClientModalForSelectedOrder,
  selectSelectedOrder,
  startAddItemFlow,
} from '@store/runningOrder';
import { Plus } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { ONBOARDING_TOUR_TARGETS } from '../../services/onboardingTourService';
import { CategoryChip } from './CategoryChip';
import { MenuItemCard } from './MenuItemCard';

export const MenuSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);
  const selectedOrder = useAppSelector(selectSelectedOrder);

  const { data: allCategories = [] } = useCategories(outletId);
  const categories = allCategories.filter((c) => c.status === 'active');
  const { data: rawItems = [] } = useMenuItems(outletId);
  const allItems = rawItems.filter((i) => i.status === 'active');

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    let items = allItems;

    if (selectedCategoryId) {
      items = items.filter((item) => item.category_id === selectedCategoryId);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) => item.name.toLowerCase().includes(q));
    }

    return items;
  }, [allItems, selectedCategoryId, searchQuery]);

  const handleCategoryClick = useCallback((categoryId: string | null) => {
    setSelectedCategoryId(categoryId);
  }, []);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => a.display_order - b.display_order);
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const query = categorySearchQuery.trim().toLowerCase();

    if (!query) return sortedCategories;

    return sortedCategories.filter((category) =>
      category.name.toLowerCase().includes(query),
    );
  }, [categorySearchQuery, sortedCategories]);

  const sectionHeaderClass = 'app-section-header';
  const canAddItemsToOrder = Boolean(selectedOrder);

  const handleMenuItemClick = useCallback(
    (item: (typeof allItems)[number]) => {
      if (!canAddItemsToOrder) {
        apiInfo('Select or create an order first');
        return;
      }

      dispatch(startAddItemFlow(item));
    },
    [canAddItemsToOrder, dispatch],
  );

  const handleOpenClientModal = useCallback(() => {
    dispatch(openClientModalForSelectedOrder());
  }, [dispatch]);

  return (
    <div
      className="flex h-full flex-col bg-white text-black"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.salesMenuPanel}
    >
      <div className="app-bg flex h-11 items-center justify-between border-b border-gray-200 px-4">
        <h3 className={sectionHeaderClass}>Categories</h3>

        <div className="relative w-44">
          <input
            type="text"
            placeholder="Search"
            value={categorySearchQuery}
            onChange={(e) => setCategorySearchQuery(e.target.value)}
            className="h-8 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
            aria-label="Search categories"
          />
        </div>
      </div>

      <div className="grid max-h-[220px] grid-cols-2 auto-rows-[48px] overflow-y-auto border-b border-gray-200 bg-white md:grid-cols-4">
        <CategoryChip
          label="ALL"
          isActive={selectedCategoryId === null}
          onClick={() => handleCategoryClick(null)}
        />
        {filteredCategories.map((category) => (
          <CategoryChip
            key={category.id}
            label={category.name}
            isActive={selectedCategoryId === category.id}
            onClick={() => handleCategoryClick(category.id)}
          />
        ))}
      </div>

      <div className="app-bg flex h-11 items-center justify-between border-b border-gray-200 px-4">
        <h3 className={sectionHeaderClass}>Menu items</h3>

        <div className="flex items-center gap-3">
          {selectedOrder ? (
            <>
              {/* <button
                type="button"
                className="flex h-8 items-center gap-1.5 rounded border border-gray-300 bg-white px-3 text-sm font-semibold uppercase tracking-wide text-black transition-colors hover:bg-gray-50"
              >
                <Utensils size={14} />
                <span>Open Dish</span>
              </button> */}

              <div className="group relative">
                <button
                  type="button"
                  onClick={handleOpenClientModal}
                  className="flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white text-black transition-colors hover:bg-gray-50"
                  aria-label="Add client to order"
                >
                  <Plus size={15} />
                </button>
                <span className="pointer-events-none absolute -bottom-8 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                  Add client to order
                </span>
              </div>
            </>
          ) : null}

          <div className="relative w-44">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 w-full rounded border border-gray-300 bg-white px-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              aria-label="Search menu items"
            />
          </div>
        </div>
      </div>

      <div className="grid flex-1 content-start grid-cols-2 overflow-y-auto bg-white md:grid-cols-4">
        {filteredItems.length === 0 ? (
          <p className="col-span-full p-6 text-center text-sm italic text-gray-500">
            No items found
          </p>
        ) : (
          filteredItems.map((item) => (
            <MenuItemCard
              key={item.id}
              item={item}
              onClick={handleMenuItemClick}
              disabled={!canAddItemsToOrder}
            />
          ))
        )}
      </div>
    </div>
  );
};
